/**
 * Inactivity lock for the unlocked chat session.
 *
 * After 15 minutes without input (any `pointerdown`, `keydown`, or `wheel`
 * event on the document), or 15 minutes with the tab hidden, `onLock` fires;
 * the caller clears the private key, cached conversation keys, and rendered
 * plaintext. On return — the next such event or the tab becoming visible,
 * whichever comes first — `onResume` fires immediately so the caller can unwrap
 * again from the device key (not lazily on decrypt).
 */

export const IDLE_LOCK_MS = 15 * 60 * 1000;
export const IDLE_INPUT_EVENTS = ['pointerdown', 'keydown', 'wheel'] as const;

export interface IdleLockHandlers {
  onLock: () => void;
  onResume: () => void;
}

export interface IdleLockOptions {
  idleMs?: number;
  now?: () => number;
  doc?: Document;
}

/** Start watching; returns a stop function that removes every listener and timer. */
export function startIdleLock(handlers: IdleLockHandlers, options: IdleLockOptions = {}): () => void {
  const idleMs = options.idleMs ?? IDLE_LOCK_MS;
  const now = options.now ?? (() => Date.now());
  const doc = options.doc ?? document;

  let locked = false;
  let stopped = false;
  let lastInputAt = now();
  let hiddenSince: number | null = doc.hidden ? now() : null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const deadline = (): number => {
    const inputDeadline = lastInputAt + idleMs;
    return hiddenSince == null ? inputDeadline : Math.min(inputDeadline, hiddenSince + idleMs);
  };

  const clearTimer = () => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const lock = () => {
    if (locked) return;
    locked = true;
    clearTimer();
    handlers.onLock();
  };

  const schedule = () => {
    clearTimer();
    if (locked || stopped) return;
    timer = setTimeout(check, Math.max(0, deadline() - now()));
  };

  const check = () => {
    timer = null;
    if (stopped || locked) return;
    if (now() >= deadline()) lock();
    else schedule();
  };

  const resume = () => {
    if (!locked) return;
    locked = false;
    handlers.onResume();
  };

  const onInput = () => {
    if (stopped) return;
    if (!locked && now() >= deadline()) lock(); // a throttled timer may not have fired yet
    lastInputAt = now();
    resume();
    schedule();
  };

  const onVisibilityChange = () => {
    if (stopped) return;
    if (doc.hidden) {
      hiddenSince = now();
      schedule();
      return;
    }
    if (!locked && now() >= deadline()) lock(); // hidden longer than the window (e.g. sleep)
    hiddenSince = null;
    lastInputAt = now();
    resume();
    schedule();
  };

  IDLE_INPUT_EVENTS.forEach((type) => doc.addEventListener(type, onInput, { capture: true, passive: true }));
  doc.addEventListener('visibilitychange', onVisibilityChange);
  schedule();

  return () => {
    stopped = true;
    clearTimer();
    IDLE_INPUT_EVENTS.forEach((type) => doc.removeEventListener(type, onInput, { capture: true }));
    doc.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
